import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { awsRum } from 'Utils/AwsRum';
import './HistoryOfPianoMusic.css';

const TIMELINE_START = 1715;
const TIMELINE_END = 1998;
const TIMELINE_SPAN = TIMELINE_END - TIMELINE_START;
const ROADMAP_WIDTH = 12000;
const ROADMAP_HEIGHT = 900;
const ROADMAP_INSET = 120;
const PATH_MIDLINE = 440;
const PATH_LANE_OFFSETS = [-310, -205, -95, 35, 170, 295];

type EraName = 'Baroque' | 'Classical' | 'Romantic' | 'Modern' | 'Jazz';

type Era = {
  name: EraName;
  start: number;
  end: number;
  summary: string;
  color: string;
};

type RepertoireNode = {
  year: number;
  composer: string;
  title: string;
  era: EraName;
  reason: string;
  lane: number;
  spotlight?: boolean;
};

type InstrumentMilestone = {
  year: number;
  title: string;
  impact: string;
};

type SourceLink = {
  label: string;
  href: string;
};

type RoadmapPoint = {
  x: number;
  y: number;
};

const eras: Era[] = [
  {
    name: 'Baroque',
    start: 1715,
    end: 1750,
    summary: 'Counterpoint, dance rhythm, ornament, and the last great harpsichord literature.',
    color: '#D4825F',
  },
  {
    name: 'Classical',
    start: 1750,
    end: 1820,
    summary: 'Phrase clarity, sonata form, singing melody, and the fortepiano taking over.',
    color: '#E1B857',
  },
  {
    name: 'Romantic',
    start: 1820,
    end: 1890,
    summary: 'The piano becomes personal, orchestral, virtuosic, and central to recital culture.',
    color: '#AF7EDE',
  },
  {
    name: 'Modern',
    start: 1888,
    end: 1998,
    summary: 'Color harmony, percussive attack, abstraction, minimalism, and new keyboard sound.',
    color: '#68C7BD',
  },
  {
    name: 'Jazz',
    start: 1899,
    end: 1984,
    summary: 'Ragtime, stride, swing, bebop, modal voicings, and electric keyboard language.',
    color: '#EF7E8E',
  },
];

const repertoirePath: RepertoireNode[] = [
  {
    year: 1717,
    composer: 'Couperin',
    title: 'Les Barricades Mysterieuses',
    era: 'Baroque',
    reason: 'Ornament, style, and clavecin elegance before the piano fully arrives.',
    lane: 1,
  },
  {
    year: 1720,
    composer: 'J. S. Bach',
    title: 'Invention No. 1 in C Major',
    era: 'Baroque',
    reason: 'Two-voice independence and contrapuntal clarity in miniature.',
    lane: 4,
  },
  {
    year: 1722,
    composer: 'J. S. Bach',
    title: 'Prelude in C Major, WTC I',
    era: 'Baroque',
    reason: 'A transparent harmonic path that still teaches hand balance.',
    lane: 2,
  },
  {
    year: 1738,
    composer: 'D. Scarlatti',
    title: 'Sonata in D Minor, K. 141',
    era: 'Baroque',
    reason: 'Repeated notes, hand crossing, and Iberian keyboard brilliance.',
    lane: 0,
  },
  {
    year: 1766,
    composer: 'C. P. E. Bach',
    title: 'Solfeggietto',
    era: 'Classical',
    reason: 'The empfindsamer style turns keyboard passagework into rhetoric.',
    lane: 3,
  },
  {
    year: 1774,
    composer: 'Haydn',
    title: 'Sonata in C Major, Hob. XVI:35',
    era: 'Classical',
    reason: 'Classical phrase design and wit in a clear sonata plan.',
    lane: 5,
  },
  {
    year: 1783,
    composer: 'Mozart',
    title: 'Rondo alla Turca, K. 331',
    era: 'Classical',
    reason: 'Public-facing character, crisp articulation, and comic propulsion.',
    lane: 1,
  },
  {
    year: 1788,
    composer: 'Mozart',
    title: 'Sonata in C Major, K. 545',
    era: 'Classical',
    reason: 'A compact entry point into clean Classical piano style.',
    lane: 4,
  },
  {
    year: 1797,
    composer: 'Clementi',
    title: 'Sonatina Op. 36 No. 1',
    era: 'Classical',
    reason: 'A practical bridge from studies into repertoire-shaped sonata writing.',
    lane: 2,
  },
  {
    year: 1798,
    composer: 'Beethoven',
    title: 'Pathetique Sonata, Op. 13',
    era: 'Classical',
    reason: 'Drama, weight, and expanded dynamic range point toward Romanticism.',
    lane: 0,
  },
  {
    year: 1801,
    composer: 'Beethoven',
    title: 'Moonlight Sonata, Op. 27 No. 2',
    era: 'Classical',
    reason: 'Texture and resonance become the emotional center of the piece.',
    lane: 3,
  },
  {
    year: 1810,
    composer: 'Beethoven',
    title: 'Fur Elise',
    era: 'Classical',
    reason: 'A familiar hook with articulation, pedaling, and character changes.',
    lane: 5,
  },
  {
    year: 1827,
    composer: 'Schubert',
    title: 'Impromptu Op. 90 No. 2',
    era: 'Romantic',
    reason: 'Song-like long lines meet repeated figuration and harmonic drift.',
    lane: 1,
  },
  {
    year: 1830,
    composer: 'Mendelssohn',
    title: 'Song Without Words Op. 19 No. 1',
    era: 'Romantic',
    reason: 'The piano learns to sing without text.',
    lane: 4,
  },
  {
    year: 1831,
    composer: 'Chopin',
    title: 'Nocturne Op. 9 No. 2',
    era: 'Romantic',
    reason: 'Bel canto phrasing, rubato, and ornament become pianistic language.',
    lane: 2,
    spotlight: true,
  },
  {
    year: 1835,
    composer: 'Chopin',
    title: 'Ballade No. 1 in G Minor, Op. 23',
    era: 'Romantic',
    reason: 'Narrative form, harmonic distance, and the piano as a dramatic storyteller.',
    lane: 0,
    spotlight: true,
  },
  {
    year: 1836,
    composer: 'Clara Schumann',
    title: 'Notturno, Op. 6 No. 2',
    era: 'Romantic',
    reason: 'Poetic character writing from a central Romantic recital innovator.',
    lane: 3,
  },
  {
    year: 1838,
    composer: 'Robert Schumann',
    title: 'Traumerei',
    era: 'Romantic',
    reason: 'A short character piece where voicing is everything.',
    lane: 4,
  },
  {
    year: 1839,
    composer: 'Chopin',
    title: 'Prelude Op. 28 No. 4',
    era: 'Romantic',
    reason: 'Economy, harmonic gravity, and expressive control in one page.',
    lane: 2,
    spotlight: true,
  },
  {
    year: 1841,
    composer: 'Fanny Hensel',
    title: 'September: At the River',
    era: 'Romantic',
    reason: 'A lyrical calendar piece with Romantic color and flow.',
    lane: 5,
  },
  {
    year: 1845,
    composer: 'Chopin',
    title: 'Barcarolle, Op. 60',
    era: 'Romantic',
    reason: 'Late Chopin: rocking motion, luminous harmony, and large-scale form.',
    lane: 0,
    spotlight: true,
  },
  {
    year: 1850,
    composer: 'Liszt',
    title: 'Liebestraum No. 3',
    era: 'Romantic',
    reason: 'Concert virtuosity fused with a long vocal line.',
    lane: 3,
  },
  {
    year: 1867,
    composer: 'Grieg',
    title: 'Arietta, Op. 12 No. 1',
    era: 'Romantic',
    reason: 'National color and intimate miniature writing.',
    lane: 1,
  },
  {
    year: 1874,
    composer: 'Mussorgsky',
    title: 'Pictures at an Exhibition: Promenade',
    era: 'Romantic',
    reason: 'The piano becomes architectural, pictorial, and orchestral.',
    lane: 5,
  },
  {
    year: 1888,
    composer: 'Satie',
    title: 'Gymnopedie No. 1',
    era: 'Modern',
    reason: 'A spare anti-virtuoso sound world opens modern color.',
    lane: 2,
  },
  {
    year: 1892,
    composer: 'Rachmaninoff',
    title: 'Prelude in C-sharp Minor',
    era: 'Romantic',
    reason: 'Late Romantic sonority, bells, and dense resonance.',
    lane: 0,
  },
  {
    year: 1892,
    composer: 'Amy Beach',
    title: 'Dreaming, Op. 15 No. 3',
    era: 'Romantic',
    reason: 'American Romantic piano writing with intimate color.',
    lane: 4,
  },
  {
    year: 1893,
    composer: 'Brahms',
    title: 'Intermezzo Op. 118 No. 2',
    era: 'Romantic',
    reason: 'Compressed late style: inner voices, warmth, and structural depth.',
    lane: 1,
  },
  {
    year: 1894,
    composer: 'Scriabin',
    title: 'Etude Op. 8 No. 12',
    era: 'Modern',
    reason: 'Romantic technique pushed toward chromatic intensity.',
    lane: 5,
  },
  {
    year: 1899,
    composer: 'Scott Joplin',
    title: 'Maple Leaf Rag',
    era: 'Jazz',
    reason: 'Syncopation becomes a written keyboard language.',
    lane: 3,
  },
  {
    year: 1901,
    composer: 'Ravel',
    title: "Jeux d'eau",
    era: 'Modern',
    reason: 'Liquid figuration and orchestral color reshape piano texture.',
    lane: 0,
  },
  {
    year: 1905,
    composer: 'Debussy',
    title: 'Clair de lune',
    era: 'Modern',
    reason: 'Color, resonance, and pedal atmosphere become the subject.',
    lane: 4,
  },
  {
    year: 1910,
    composer: 'Debussy',
    title: 'La cathedrale engloutie',
    era: 'Modern',
    reason: 'Parallel harmony and register build an imagined acoustic space.',
    lane: 2,
  },
  {
    year: 1911,
    composer: 'Bartok',
    title: 'Allegro barbaro',
    era: 'Modern',
    reason: 'Percussive rhythm and folk energy hit the modern piano.',
    lane: 5,
  },
  {
    year: 1911,
    composer: 'Schoenberg',
    title: 'Six Little Piano Pieces, Op. 19',
    era: 'Modern',
    reason: 'Extreme compression and atonality in miniature.',
    lane: 1,
  },
  {
    year: 1912,
    composer: 'Prokofiev',
    title: 'Toccata, Op. 11',
    era: 'Modern',
    reason: 'Motor rhythm and steel-edged virtuosity.',
    lane: 3,
  },
  {
    year: 1921,
    composer: 'Stravinsky',
    title: 'Three Movements from Petrushka',
    era: 'Modern',
    reason: 'Ballet color translated into a massive piano challenge.',
    lane: 0,
  },
  {
    year: 1921,
    composer: 'James P. Johnson',
    title: 'Carolina Shout',
    era: 'Jazz',
    reason: 'Stride piano enters the virtuoso keyboard lineage.',
    lane: 4,
  },
  {
    year: 1929,
    composer: 'Fats Waller',
    title: "Ain't Misbehavin'",
    era: 'Jazz',
    reason: 'Stride charm, harmonic snap, and song-form keyboard style.',
    lane: 2,
  },
  {
    year: 1930,
    composer: 'Copland',
    title: 'Piano Variations',
    era: 'Modern',
    reason: 'Lean modernist architecture and severe sonority.',
    lane: 5,
  },
  {
    year: 1933,
    composer: 'Art Tatum',
    title: 'Tea for Two',
    era: 'Jazz',
    reason: 'Reharmonization and velocity redraw what solo jazz piano can be.',
    lane: 1,
  },
  {
    year: 1944,
    composer: 'Thelonious Monk',
    title: 'Round Midnight',
    era: 'Jazz',
    reason: 'Angular melody, silence, and dissonant voicing become unmistakable style.',
    lane: 3,
  },
  {
    year: 1946,
    composer: 'John Cage',
    title: 'Sonatas and Interludes',
    era: 'Modern',
    reason: 'Prepared piano turns the instrument into a percussion orchestra.',
    lane: 0,
  },
  {
    year: 1951,
    composer: 'Bud Powell',
    title: 'Un Poco Loco',
    era: 'Jazz',
    reason: 'Bebop language crystallizes at the keyboard.',
    lane: 5,
  },
  {
    year: 1953,
    composer: 'Ligeti',
    title: 'Musica ricercata',
    era: 'Modern',
    reason: 'Restriction becomes process, rhythm, and modern drama.',
    lane: 2,
  },
  {
    year: 1958,
    composer: 'Bill Evans',
    title: 'Peace Piece',
    era: 'Jazz',
    reason: 'Modal harmony and repeated ground meet lyrical improvisation.',
    lane: 4,
  },
  {
    year: 1965,
    composer: 'Herbie Hancock',
    title: 'Maiden Voyage',
    era: 'Jazz',
    reason: 'Quartal/modal voicings point toward a modern jazz piano vocabulary.',
    lane: 1,
  },
  {
    year: 1975,
    composer: 'Keith Jarrett',
    title: 'The Koln Concert, Part I',
    era: 'Jazz',
    reason: 'Solo improvisation becomes a large-form concert statement.',
    lane: 5,
  },
  {
    year: 1984,
    composer: 'Chick Corea',
    title: "Children's Songs",
    era: 'Jazz',
    reason: 'Minimal cells, jazz harmony, and classical craft meet.',
    lane: 3,
  },
  {
    year: 1995,
    composer: 'Philip Glass',
    title: 'Etudes, Book 1',
    era: 'Modern',
    reason: 'Minimalist process enters the mainstream piano repertoire.',
    lane: 0,
  },
];

const instrumentMilestones: InstrumentMilestone[] = [
  {
    year: 1700,
    title: 'Cristofori pianoforte',
    impact: 'Hammer action creates touch-sensitive soft and loud playing.',
  },
  {
    year: 1821,
    title: 'Double escapement',
    impact: 'Fast repetition feeds Romantic virtuosity.',
  },
  {
    year: 1825,
    title: 'Iron frame',
    impact: 'More tension, more projection, larger halls.',
  },
  {
    year: 1859,
    title: 'Overstrung grand',
    impact: 'Longer bass strings and deeper resonance.',
  },
  {
    year: 1890,
    title: '88-key standard',
    impact: 'A0-C8 becomes the modern concert compass.',
  },
  {
    year: 1939,
    title: 'Novachord',
    impact: 'Electronic keyboard color joins the story.',
  },
  {
    year: 1983,
    title: 'MIDI and DX7',
    impact: 'Digital control and FM tone change keyboard writing.',
  },
];

const sourceLinks: SourceLink[] = [
  {
    label: 'PTNA: Chopin Ballade No. 1',
    href: 'https://enc.piano.or.jp/en/musics/462',
  },
  {
    label: 'PTNA: Chopin Barcarolle',
    href: 'https://enc.piano.or.jp/en/musics/543',
  },
  {
    label: 'The Met: Cristofori pianofortes',
    href: 'https://www.metmuseum.org/toah/hd/cris/hd_cris.htm',
  },
  {
    label: 'The Met: Babcock iron frame',
    href: 'https://www.metmuseum.org/art/collection/search/504602',
  },
  {
    label: 'Smithsonian: Steinway overstringing',
    href: 'https://americanhistory.si.edu/steinwaydiary/annotations/?id=2043&popup=1',
  },
  {
    label: 'Yamaha: 88-key standard',
    href: 'https://hub.yamaha.com/pianos/p-acoustic/why-arent-there-more-than-88-keys-on-a-piano/',
  },
];

const yearMarkers = [1720, 1760, 1800, 1840, 1880, 1920, 1960, 2000];

const yearToX = (year: number) => {
  const boundedYear = Math.min(Math.max(year, TIMELINE_START), TIMELINE_END);
  const usableWidth = ROADMAP_WIDTH - ROADMAP_INSET * 2;
  return ROADMAP_INSET + ((boundedYear - TIMELINE_START) / TIMELINE_SPAN) * usableWidth;
};

const nodeToPoint = (node: RepertoireNode): RoadmapPoint => ({
  x: yearToX(node.year),
  y: PATH_MIDLINE + PATH_LANE_OFFSETS[node.lane],
});

const createPathD = (points: RoadmapPoint[]) => {
  if (points.length === 0) return '';

  return points.slice(1).reduce((path, point, index) => {
    const previousPoint = points[index];
    const controlX = previousPoint.x + (point.x - previousPoint.x) / 2;
    return `${path} C ${controlX} ${previousPoint.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
  }, `M ${points[0].x} ${points[0].y}`);
};

const getEraColor = (eraName: EraName) =>
  eras.find((era) => era.name === eraName)?.color || '#77c9b5';

const HistoryOfPianoMusic = () => {
  const location = useLocation();
  const [showInfo, setShowInfo] = useState(false);

  const roadmapPoints = useMemo(() => repertoirePath.map(nodeToPoint), []);
  const roadmapPathD = useMemo(() => createPathD(roadmapPoints), [roadmapPoints]);

  useEffect(() => {
    awsRum?.recordPageView(location.pathname);
  }, [location.pathname]);

  return (
    <div className="history-page">
      <Menu selected="History" />
      <main className="history-main">
        <section className="history-hero">
          <div className="history-hero-copy">
            <div className="history-title-row">
              <h1>History of Piano Music</h1>
              <button
                className="history-info-button"
                type="button"
                aria-label="About this roadmap"
                aria-expanded={showInfo}
                aria-controls="history-info-panel"
                onClick={() => setShowInfo((current) => !current)}
              >
                i
              </button>
            </div>
            {showInfo && (
              <p className="history-intro" id="history-info-panel">
                A chronological path through the keyboard pieces that changed piano writing, from
                Bach and Scarlatti to Chopin, Debussy, Monk, Hancock, and Glass.
              </p>
            )}
          </div>
        </section>

        <section className="history-roadmap-shell" aria-label="Piano repertoire roadmap">
          <div className="history-roadmap-scroll">
            <div
              className="history-roadmap-stage"
              style={{
                width: `${ROADMAP_WIDTH}px`,
                height: `${ROADMAP_HEIGHT}px`,
              }}
            >
              <div className="history-era-backdrop" aria-hidden="true">
                {eras.map((era) => (
                  <div
                    className="history-era-window"
                    key={era.name}
                    style={{
                      left: `${yearToX(era.start)}px`,
                      width: `${Math.max(yearToX(era.end) - yearToX(era.start), 50)}px`,
                      borderColor: era.color,
                    }}
                  >
                    <span style={{ color: era.color }}>{era.name}</span>
                    <p>{era.summary}</p>
                  </div>
                ))}
              </div>

              <div className="history-year-axis" aria-hidden="true">
                {yearMarkers.map((year) => (
                  <div
                    className="history-year-marker"
                    key={year}
                    style={{ left: `${yearToX(year)}px` }}
                  >
                    <span>{year}</span>
                  </div>
                ))}
              </div>

              <svg
                className="history-path-svg"
                width={ROADMAP_WIDTH}
                height={ROADMAP_HEIGHT}
                viewBox={`0 0 ${ROADMAP_WIDTH} ${ROADMAP_HEIGHT}`}
                aria-hidden="true"
              >
                <path className="history-path-shadow" d={roadmapPathD} />
                <path className="history-path-line" d={roadmapPathD} />
              </svg>

              {instrumentMilestones.map((milestone) => (
                <article
                  className="history-instrument-flag"
                  key={`${milestone.year}-${milestone.title}`}
                  style={{ left: `${yearToX(milestone.year)}px` }}
                >
                  <div className="history-instrument-pin" />
                  <span>{milestone.year}</span>
                  <strong>{milestone.title}</strong>
                  <p>{milestone.impact}</p>
                </article>
              ))}

              {repertoirePath.map((node, index) => {
                const point = nodeToPoint(node);
                const isUpper = point.y < PATH_MIDLINE;
                let labelClass = isUpper ? 'label-above' : 'label-below';
                if (point.y < 250) {
                  labelClass = 'label-below';
                } else if (point.y > 660) {
                  labelClass = 'label-above';
                }
                const eraColor = getEraColor(node.era);

                return (
                  <article
                    className={`history-path-node ${isUpper ? 'is-upper' : 'is-lower'} ${
                      node.spotlight ? 'is-spotlight' : ''
                    }`}
                    key={`${node.year}-${node.composer}-${node.title}`}
                    aria-label={`${node.year}: ${node.title} by ${node.composer}. ${node.reason}`}
                    style={{
                      left: `${point.x}px`,
                      top: `${point.y}px`,
                    }}
                  >
                    <div
                      className="history-node-orb"
                      style={{ backgroundColor: eraColor, borderColor: eraColor }}
                    >
                      <span>{index + 1}</span>
                    </div>
                    <div className={`history-node-label ${labelClass}`}>
                      <span className="history-node-year">{node.year}</span>
                      <h3>{node.title}</h3>
                      <p className="history-node-composer">{node.composer}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="history-composer-map" aria-label="Composer and piece map">
          <div className="history-section-heading">
            <p className="history-kicker">Composer map</p>
            <h2>What the path covers</h2>
          </div>
          <div className="history-composer-grid">
            {eras.map((era) => {
              const nodes = repertoirePath.filter((node) => node.era === era.name);
              return (
                <article key={era.name}>
                  <div className="history-era-title">
                    <span style={{ backgroundColor: era.color }} />
                    <h3>{era.name}</h3>
                  </div>
                  <p>{era.summary}</p>
                  <ul>
                    {nodes.slice(0, 7).map((node) => (
                      <li key={`${era.name}-${node.composer}-${node.title}`}>
                        <strong>{node.composer}</strong>
                        <span>{node.title}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </section>

        <footer className="history-sources">
          <span>Reference notes:</span>
          {sourceLinks.map((source) => (
            <a key={source.href} href={source.href} target="_blank" rel="noreferrer">
              {source.label}
            </a>
          ))}
        </footer>
      </main>
    </div>
  );
};

export default HistoryOfPianoMusic;
